import {
  slab, roof, wallX, wallZ, goal, shard, checkpoint, sign, neon, lift,
  edgeLights,
} from '../kit.js';

const signal = (x, y, z, yaw = 0) => [
  shard(x, y + 1.6, z), checkpoint(x, y, z, yaw),
  neon(x, y + 0.08, z, [3, 0.12, 3], 'lime'),
];

// The underworks doubles back under the intake. Exposed tile edges become
// walls, while adjoining cells share a full-width opening at each turn.
const cells = [[2, 1], [2, 2], [1, 2], [0, 2], [-1, 2], [-2, 2],
  [-2, 1], [-2, 0], [-2, -1], [-2, -2], [-2, -3], [-2, -4], [-1, -4], [0, -4]];
const occupied = new Set(cells.map(([x, z]) => `${x},${z}`));
const tunnel = cells.flatMap(([tx, tz]) => {
  const x = tx * 12, z = tz * 12;
  const out = [slab(x, -4, z, 12, 12),
    neon(x, -3.92, z, [2, 0.1, 2], 'amber')];
  if (!(tx === 2 && tz === 1) && !(tz === -4 && tx >= -1)) {
    out.push(roof(x, 6, z, 12, 12, 'trim'));
  }
  for (const dx of [-1, 1]) if (!occupied.has(`${tx + dx},${tz}`)) {
    out.push(wallX(x + dx * 6.6, -4, z, 12, 10, 'plate'));
  }
  for (const dz of [-1, 1]) if (!occupied.has(`${tx},${tz + dz}`)) {
    out.push(wallZ(x, -4, z + dz * 6.6, 12, 10, 'plate'));
  }
  return out;
});

export default {
  id: 'switchback',
  name: 'SWITCHBACK',
  blurb: 'Below the entrance. Back above it. An enclosed trial loop.',
  unlockAfter: 'skybreak',
  mode: { type: 'collect', par: 150 },
  redline: { seconds: 135 },
  spawn: { p: [-10, 24.4, 28], yaw: 0 },
  killY: -85,
  env: {
    fog: 0x070e18, fogDensity: 0.0015,
    sky: { zenith: 0x03060c, horizon: 0x111b2b, glow: 0x184156, aurora: 0x244b68 },
    floorY: -150,
  },
  objects: [
    // INTAKE, middle floor. The final walkway is visible overhead, but
    // the room has no portalable walls that could skip the ascent.
    slab(0, 24, 16, 36, 44), roof(0, 68, 16, 36, 44, 'trim'),
    wallX(-18.6, 24, 16, 44, 44, 'plate'),
    wallZ(0, 24, 38.6, 38.4, 44, 'plate'),
    wallX(18.6, 24, 0, 12, 44, 'plate'),
    wallX(18.6, 24, 28, 20, 44, 'plate'),
    wallX(18.6, 36, 12, 12, 32, 'plate'),
    ...edgeLights(0, 24, 16, 36, 44),
    sign(-10, 27.8, 19, 0, ['SWITCHBACK', 'FOUR SIGNALS. THREE FLOORS.', 'RETURN ABOVE THIS ROOM']),
    sign(10, 28, 12, -90, ['02 // UNDERWORKS', 'DESCEND THROUGH THE SIDE DOOR'], '#ffb020'),

    // The observation balcony projects through the shaft wall, keeping
    // the receiver floor in view from a safe standing position.
    slab(0, 24, -9, 12, 6),
    ...signal(0, 24, -9),
    sign(0, 27.6, -10.5, 0, ['01 // INTAKE', 'BLUE FLOOR IS YOUR RETURN', 'UNDERWORKS DOOR TO THE RIGHT']),

    // CORE. The south wall has a middle doorway and an upper return.
    // Floors, openings and shot paths are genuinely separated in height.
    slab(0, -44, -30, 36, 48, 'panel'),
    roof(0, 86, -30, 36, 48, 'trim'),
    wallZ(0, -44, -54.6, 38.4, 130, 'plate'),
    wallX(18.6, -44, -30, 48, 130, 'plate'),
    wallX(-18.6, -44, -24, 36, 130, 'plate'),
    wallX(-18.6, -44, -48, 12, 40, 'plate'),
    wallX(-18.6, 30, -48, 12, 56, 'plate'),
    wallZ(0, -44, -5.4, 36, 68, 'plate'),
    wallZ(-12, 24, -5.4, 12, 12, 'plate'),
    wallZ(12, 24, -5.4, 12, 12, 'plate'),
    wallZ(0, 36, -5.4, 36, 16, 'plate'),
    wallZ(0, 64, -5.4, 36, 22, 'plate'),
    neon(-17.8, 20, -30, [0.12, 116, 0.12], 'magenta'),
    neon(17.8, 20, -30, [0.12, 116, 0.12], 'magenta'),

    // Return receiver. Its top is visible from both observation decks.
    // The thin receiver edges cannot fit a side-facing portal.
    slab(8, 4, -32, 6, 4, 'panel'),
    ...edgeLights(8, 4, -32, 6, 4),
    sign(8, 6.4, -35, 180, ['RETURN RECEIVER', 'EXIT POINTS UP'], '#2ff3ff'),

    // UNDERWORKS. A safe drop feeds the tunnel beneath the starting room.
    ...tunnel,
    wallX(30.6, 6, 12, 12, 32, 'plate'),
    wallX(18.6, 6, 12, 12, 18, 'plate'),
    wallZ(24, 6, 5.4, 12, 32, 'plate'),
    wallZ(24, 6, 18.6, 12, 32, 'plate'),
    roof(24, 38, 12, 12, 12, 'trim'),
    ...signal(0, -4, 24, 90),
    sign(24, -0.4, 25, 180, ['02 // UNDERWORKS', 'FOLLOW THE AMBER FLOOR'], '#ffb020'),
    sign(-24, -0.4, 18, 0, ['BELOW THE INTAKE', 'RETURN SHAFT AHEAD'], '#ffb020'),
    roof(-18, 30, -48, 24, 12, 'trim'),
    wallX(-30.6, 6, -48, 12, 24, 'plate'),
    wallZ(-24, 6, -54.6, 12, 24, 'plate'),

    // Lift out of the tunnel onto a fresh setup balcony. A reset here
    // can replace both portals without revisiting any earlier room.
    lift(-12, -4, -48, 25, 2.3),
    slab(6, 16, -48, 24, 12),
    ...edgeLights(6, 16, -48, 24, 12, 'amber'),
    ...signal(0, 16, -48, 180),
    sign(0, 19.6, -43.5, 180, ['03 // RETURN ASCENT', 'BLUE FLOOR: EXIT. DEEP FLOOR: ENTRY.', 'FALL, THEN STEER TOWARD THE INTAKE'], '#ffb020'),

    // Upper return. The receiving ledge catches the vertical launch;
    // the right-hand mezzanine leads back above the original spawn.
    slab(0, 52, -12, 36, 12),
    ...edgeLights(0, 52, -12, 36, 12, 'lime'),
    checkpoint(0, 52, -12, 180),
    slab(12, 52, 16, 12, 44),
    ...edgeLights(12, 52, 16, 12, 44, 'lime'),
    ...signal(12, 52, 24, 180),
    sign(12, 55.6, 12, 180, ['04 // OVER THE INTAKE', 'YOU STARTED DOWN THERE'], '#9dff3c'),
    goal(12, 52, 32, 180),
  ],
};
